// src/components/LoginModal.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function LoginModal({ onClose, onLoginSuccess, message, onOpenRegister }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showModalContent, setShowModalContent] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState('');

  const [showResend, setShowResend] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowModalContent(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const openRegisterAndCloseLogin = () => {
    onClose?.();
    onOpenRegister?.();
  };

  const renderRegisterCta = (inline = false) => (
    <button
      type="button"
      className="btn-secondary"
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontSize: inline ? 'inherit' : '0.95rem',
        marginLeft: inline ? '0.25rem' : 0,
      }}
      onClick={openRegisterAndCloseLogin}
    >
      Registreer opnieuw
    </button>
  );

  const buildInvalidLoginError = () => (
    <>
      <strong>E-mailadres of wachtwoord is onjuist.</strong>
      <br />
      <span style={{ color: '#6c757d' }}>
        Gebruik je misschien nog inloggegevens van de{' '}
        <em>oude</em> Doorloopjes-website? Die werken niet meer op deze vernieuwde site.
      </span>{' '}
      {renderRegisterCta(true)}
    </>
  );

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await axios.post(`${STRAPI_URL}/api/auth/local`, {
        identifier,
        password,
      });
      localStorage.setItem('jwt', response.data.jwt);
      onLoginSuccess();
    } catch (err) {
      const msg = (err.response?.data?.error?.message || '').toLowerCase();
      const status = err.response?.status;

      if (msg.includes('confirmed')) {
        setError(
          <>
            Je account is nog niet bevestigd.{' '}
            <button
              type="button"
              className="btn-secondary"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => setShowResend(true)}
            >
              Verstuur bevestigingsmail opnieuw
            </button>
          </>
        );
        return;
      }

      const looksClassicInvalid =
        msg.includes('invalid identifier or password') ||
        (status === 400 && (msg.includes('invalid') || msg.includes('identifier') || msg.includes('password')));

      if (looksClassicInvalid) {
        setError(buildInvalidLoginError());
        return;
      }

      const looksUnknownOrNotFound =
        status === 404 || msg.includes('not found') || msg.includes('unknown') || msg.includes('user not found');

      if (looksUnknownOrNotFound) {
        setError(buildInvalidLoginError());
        return;
      }

      setError('Inloggen lukte niet. Probeer het later opnieuw.');
    }
  };

  const handleForgotSubmit = async () => {
    setForgotStatus('');
    try {
      await axios.post(`${STRAPI_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotStatus(
        'Als dit e-mailadres bekend is, is er een resetlink verstuurd. '
        + 'Let op: accounts van de oude Doorloopjes-site werken hier niet.'
      );
    } catch (e) {
      setForgotStatus(
        <>
          Versturen lukte niet. Probeer het later opnieuw. <br />
          <span style={{ color: '#6c757d' }}>
            Heb je al een account op deze nieuwe site?
          </span>{' '}
          {renderRegisterCta(true)}
        </>
      );
    }
  };

  const handleResendSubmit = async () => {
    setResendStatus('');
    try {
      await axios.post(`${STRAPI_URL}/api/auth/send-email-confirmation`, { email: resendEmail });
      setResendStatus('Als dit e-mailadres bekend is, is er een nieuwe bevestigingsmail verstuurd.');
    } catch (e) {
      setResendStatus('Versturen lukte niet. Probeer het later opnieuw.');
    }
  };

  return (
    <div
      className={`modal-backdrop ${showModalContent ? 'show' : ''}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Inloggen"
    >
      <div
        className={`modal-content ${showModalContent ? 'show' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            {showForgot
              ? 'Wachtwoord vergeten'
              : showResend
              ? 'Bevestigingsmail opnieuw versturen'
              : 'Inloggen'}
          </h2>
          <button className="modal-close-btn" onClick={onClose} aria-label="Sluiten">
            ×
          </button>
        </div>

        {!showForgot && !showResend && (
          <p className="info-message" role="note" style={{ textAlign: 'left' }}>
            <strong>Nieuw platform:</strong> accounts van de <em>oude</em> Doorloopjes-site
            werken hier niet. Heb je nog geen account op deze site? {renderRegisterCta(true)}.
          </p>
        )}

        {!showForgot && !showResend && (
          <form onSubmit={handleLoginSubmit} autoComplete="on">
            {message && <p className="info-message">{message}</p>}

            <div className="form-group">
              <label htmlFor="login-email">E-mailadres</label>
              <input
                type="email"
                id="login-email"
                name="username"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">Wachtwoord</label>
              <input
                type="password"
                id="login-password"
                name="current-password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {/* Links netjes in een aparte row met spacing */}
            <div className="login-links-row">
              <button
                type="button"
                className="btn-secondary"
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  textDecoration: 'underline',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
                onClick={() => setShowForgot(true)}
              >
                Wachtwoord vergeten?
              </button>

              <button
                type="button"
                className="btn-secondary"
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  textDecoration: 'underline',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
                onClick={openRegisterAndCloseLogin}
              >
                Nieuw op deze site? Registreer
              </button>
            </div>

            {error && (
              <p className="error-message" role="alert" aria-live="assertive">
                {error}
              </p>
            )}
            <button type="submit" className="btn-primary">
              Inloggen
            </button>
          </form>
        )}

        {showForgot && (
          <div>
            <p className="info-message">
              Vul je e-mailadres in. Kennen we dit adres, dan krijg je een mail met een link voor een nieuw wachtwoord.
              <br />
              <span style={{ color: '#6c757d' }}>
                Had je alleen een account op de oude site?
              </span>{' '}
              {renderRegisterCta(true)}
            </p>
            <div className="form-group">
              <label htmlFor="forgot-email">E-mailadres</label>
              <input
                type="email"
                id="forgot-email"
                name="email"
                autoComplete="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn-secondary" onClick={() => setShowForgot(false)}>
                Annuleren
              </button>
              <button type="button" className="btn-primary" onClick={handleForgotSubmit}>
                Stuur resetlink
              </button>
            </div>
            {forgotStatus && (
              <p style={{ marginTop: '0.75rem' }} aria-live="polite">
                {forgotStatus}
              </p>
            )}
          </div>
        )}

        {showResend && (
          <div>
            <p className="info-message">
              Vul je e-mailadres in om de bevestigingsmail opnieuw te ontvangen.
            </p>
            <div className="form-group">
              <label htmlFor="resend-email">E-mailadres</label>
              <input
                type="email"
                id="resend-email"
                name="email"
                autoComplete="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn-secondary" onClick={() => setShowResend(false)}>
                Annuleren
              </button>
              <button type="button" className="btn-primary" onClick={handleResendSubmit}>
                Verstuur opnieuw
              </button>
            </div>
            {resendStatus && (
              <p style={{ marginTop: '0.75rem' }} aria-live="polite">
                {resendStatus}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginModal;
